/* eslint-disable import/no-extraneous-dependencies */
import isPropValid from '@emotion/is-prop-valid';
import { DehydratedState, HydrationBoundary, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { GlobalThemeComponent } from '@tvg/fuse-design/themes';
import { PageType, PLPURL } from '@tvg/product-listing-url';
import Main from 'client/pages/Main';
import React, { useMemo } from 'react';
import { StyleSheetManager, WebTarget } from 'styled-components';
import ErrorBoundary from './pages/ErrorBoundary/ErrorBoundary';
import { GlobalConfigProvider } from './state/GlobalConfig/GlobalConfig';
import { MainStoreProvider } from './state/Main';

function shouldForwardProp(propName: string, target: WebTarget | undefined) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
}

export interface AppProps {
  pageType: PageType;
  brand: string;
  apiURL: string;
  envURL: string;
  queryClient: QueryClient;
  isLocal: boolean;
  queryState?: DehydratedState;
  plpurl: PLPURL;
  isSFLVeryIEHeaderEnabled?: boolean; // Optional prop for SFL Very IE header
}

export default function App({
  pageType,
  apiURL,
  brand,
  envURL,
  queryClient,
  isLocal,
  queryState,
  plpurl,
  isSFLVeryIEHeaderEnabled,
}: AppProps) {
  const globalConfig = useMemo(
    () => ({ pageType, apiURL, brand, envURL, isLocal, isSFLVeryIEHeaderEnabled }),
    [apiURL, brand, envURL, isLocal, pageType, isSFLVeryIEHeaderEnabled],
  );
  return (
    <StyleSheetManager enableVendorPrefixes shouldForwardProp={shouldForwardProp}>
      <ErrorBoundary>
        <GlobalConfigProvider value={globalConfig}>
          <MainStoreProvider plpURL={plpurl}>
            <QueryClientProvider client={queryClient}>
              <HydrationBoundary state={queryState}>
                {process.env.NODE_ENV !== 'production' && <GlobalThemeComponent brand={brand} />}
                <Main />
              </HydrationBoundary>
              <ReactQueryDevtools />
            </QueryClientProvider>
          </MainStoreProvider>
        </GlobalConfigProvider>
      </ErrorBoundary>
    </StyleSheetManager>
  );
}
